import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useGuestContext } from "@/context/GuestContext";

/**
 * ReturnRouter — Dispatcher page.
 * Reads the AI-classified return category and redirects to the correct resolution page.
 *   fix / defect  →  /resolve-fix
 *   damage        →  /resolve-damage
 *   preference    →  /resolve-preference
 */
const ReturnRouter: React.FC = () => {
  const { id: orderId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const productId = searchParams.get("productId");
  const navigate = useNavigate();
  const { loginType } = useAuth();
  const isGuest = loginType === "token";
  const { fetchOnboardingData } = useGuestContext();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) return;

    const route = async () => {
      try {
        const data = await fetchOnboardingData(orderId);
        // Find the correct return record matching the productId
        const returnRecord = productId 
          ? data?.returns?.find((r: any) => r.product_id === productId)
          : data?.returns?.[0];
          
        const category = (returnRecord?.category || returnRecord?.reason || "").toLowerCase();
        const queryStr = productId ? `?productId=${productId}` : "";

        if (category.includes("damage")) {
          navigate(`/customer/orders/${orderId}/resolve-damage${queryStr}`, { replace: true });
        } else if (category.includes("preference")) {
          navigate(`/customer/orders/${orderId}/resolve-preference${queryStr}`, { replace: true });
        } else {
          // fix / defect / other
          navigate(`/customer/orders/${orderId}/resolve-fix${queryStr}`, { replace: true });
        }
      } catch {
        setError("Failed to load return details. Please try again.");
      }
    };

    route();
  }, [orderId]);

  if (error) {
    return (
      <div className="tw-bg-[#1a1a1a] tw-min-h-screen tw-flex tw-items-center tw-justify-center tw-p-6">
        <div className="tw-max-w-md tw-w-full tw-bg-[#252525] tw-rounded-2xl tw-p-8 tw-text-center">
          <AlertTriangle className="tw-w-16 tw-h-16 tw-text-orange-500 tw-mx-auto tw-mb-4" />
          <h2 className="tw-text-white tw-text-xl tw-font-bold tw-mb-2">Something went wrong</h2>
          <p className="tw-text-gray-400 tw-mb-6">{error}</p>
          <button
            onClick={() => {
              if (isGuest && orderId) {
                fetchOnboardingData(orderId, true);
              }
              return isGuest ? navigate(`/customer/orders/${orderId}`) : navigate("/customer/orders");
            }}
            className="tw-w-full tw-py-3 tw-bg-[#00C5FB] tw-text-black tw-font-bold tw-rounded-xl tw-border-none tw-cursor-pointer"
          >
            {isGuest ? "Back to Order" : "Back to Orders"}
          </button>

        </div>
      </div>
    );
  }

  return (
    <div className="tw-bg-[#1a1a1a] tw-min-h-screen tw-flex tw-items-center tw-justify-center">
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
        <div className="tw-w-12 tw-h-12 tw-border-4 tw-border-[#00C5FB] tw-border-t-transparent tw-rounded-full tw-animate-spin" />
        <p className="tw-text-gray-400 tw-text-sm">Loading resolution options...</p>
      </div>
    </div>
  );
};

export default ReturnRouter;
